import { useEffect, useRef } from 'react';

export default function useVoiceMemoPlayer(options: {
	source: Blob | string;
	onPlay: () => void;
	onStop: () => void;
	onProgress: (progress: number, time: number) => void;
}) {
	const player = useRef(
		{} as {
			element?: HTMLAudioElement;
			url?: string;
			promise?: Promise<void>;
		},
	).current;

	const onPlay = useRef(options.onPlay);
	onPlay.current = options.onPlay;

	const onStop = useRef(options.onStop);
	onStop.current = options.onStop;

	const onProgress = useRef(options.onProgress);
	onProgress.current = options.onProgress;

	useEffect(() => {
		const element = document.createElement('audio');
		element.style.display = 'none';
		document.body.appendChild(element);

		element.onplay = () => onPlay.current();
		element.onpause = () => onStop.current();
		element.onended = () => {
			element.currentTime = 0;
			onStop.current();
		};
		element.ontimeupdate = () => {
			if (element.duration === 0.0) {
				return;
			}

			onProgress.current(element.currentTime / element.duration, element.currentTime);
		};

		player.element = element;

		return () => {
			if (player.url !== undefined) {
				URL.revokeObjectURL(player.url);
			}

			element.remove();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	async function lazy(): Promise<void> {
		await new Promise((resolve) => {
			if (!player.element) {
				return;
			}

			if (typeof options.source === 'string') {
				player.element.src = options.source;
			} else {
				player.url = URL.createObjectURL(options.source);
				player.element.src = player.url;
			}

			player.element.oncanplay = () => resolve(void 0);
			player.element.onerror = console.error;
			player.element.load();
		});
	}

	return {
		play: async () => {
			if (!player.promise) {
				player.promise = lazy();
			}

			await player.promise;
			player.element?.play();
		},
		pause: () => {
			player.element?.pause();
		},
	};
}
